<template>
  <div class="space-y-5">
    <div class="acc-glass rounded-2xl border border-white/10 p-5">
      <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div class="text-lg font-extrabold tracking-tight">Нагороди</div>
          <div class="mt-1 text-sm text-slate-600 dark:text-white/65">
            Колекція нагород, досягнень і тематичних серій.
          </div>
        </div>
        <div class="w-fit rounded-full border border-[#c56925]/25 bg-[#c56925]/10 px-3 py-1.5 text-sm text-slate-600 dark:text-white/65">
          <span class="font-extrabold text-slate-900 dark:text-white">
            Зібрано {{ catalog.earned_count }} · У каталозі {{ catalog.catalog_count }}
          </span>
        </div>
      </div>
    </div>

    <template v-if="catalog.catalog_count > 0">
      <section
        v-for="section in sections"
        :key="section.key"
        class="acc-glass rounded-2xl border border-white/10 p-4 sm:p-5"
      >
        <div class="mb-4 flex flex-col gap-2 border-b border-[#5b382a]/10 pb-4 sm:flex-row sm:items-start sm:justify-between dark:border-white/10">
          <div class="min-w-0">
            <h2 class="text-base font-extrabold sm:text-lg">{{ section.name }}</h2>
            <p v-if="section.description" class="mt-1 text-sm text-slate-600 dark:text-white/65">
              {{ section.description }}
            </p>
          </div>

          <div v-if="section.isSeries" class="shrink-0 text-sm text-slate-600 dark:text-white/65">
            <span class="font-bold text-slate-900 dark:text-white">{{ sectionProgress(section) }}</span>
            <span
              v-if="isCompletedSeries(section)"
              class="mt-1 block text-xs font-semibold text-[#c56925] dark:text-orange-300"
            >
              Колекцію завершено
            </span>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <button
            v-for="award in section.awards"
            :key="`award-card-${award.id}`"
            type="button"
            class="h-full rounded-2xl border p-4 text-left transition focus:outline-none focus:ring-2 focus:ring-orange-400/70 sm:p-5"
            :class="awardCardClasses(award)"
            @click="openAwardModal(award)"
          >
            <div class="flex items-center gap-4">
              <div
                class="award-badge award-badge--card relative overflow-visible rounded-full border-2 shadow-sm"
                :class="awardBadgeClasses(award)"
              >
                <div class="award-badge__surface">
                  <img
                    v-if="hasAwardImage(award)"
                    :src="award.image"
                    :alt="award.name"
                    class="award-badge__art object-contain"
                    :class="awardArtworkClasses(award)"
                    @error="markImageFailed(award.id)"
                  />
                  <div
                    v-else
                    class="award-badge__art flex items-center justify-center text-3xl font-extrabold text-white sm:text-4xl"
                    :class="awardArtworkClasses(award)"
                    :style="{ backgroundColor: award.color || 'rgb(var(--foxy-brand))' }"
                  >
                    {{ awardInitial(award) }}
                  </div>
                </div>
                <span
                  v-if="shouldShowAwardLock(award)"
                  class="award-badge__lock flex items-center justify-center rounded-full border-2 border-foxy-background bg-foxy-cocoa text-white shadow-sm"
                  :class="awardLockClasses(award)"
                  aria-hidden="true"
                >
                  <i class="fas fa-lock !text-[#fffaf5] dark:!text-white" aria-hidden="true"></i>
                </span>
              </div>

              <div class="min-w-0 flex-1">
                <div class="text-base font-extrabold leading-snug">{{ award.name }}</div>
                <div
                  class="mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold"
                  :class="awardStatusClasses(award)"
                >
                  {{ awardStatus(award) }}
                </div>
                <div v-if="award.points > 0" class="mt-1 text-xs text-slate-600 dark:text-white/60">
                  +{{ formatCount(award.points, 'бал', 'бали', 'балів') }}
                </div>
              </div>
            </div>

            <p v-if="award.description" class="mt-4 line-clamp-3 text-sm text-slate-700 dark:text-white/75">
              {{ award.description }}
            </p>
            <div
              v-if="awardPresentationState(award) === 'earned' && (award.awarded_at_human || award.awarded_at)"
              class="mt-3 text-xs text-slate-500 dark:text-white/55"
            >
              Отримано: {{ award.awarded_at_human || award.awarded_at }}
            </div>
          </button>
        </div>
      </section>
    </template>

    <div v-else class="acc-glass rounded-2xl border border-white/10 p-5">
      <div class="text-sm font-bold">Каталог нагород поки порожній</div>
      <div class="mt-1 text-xs text-slate-600 dark:text-white/60">
        Тут з’являться особливі нагороди та тематичні серії FoxyBooks.
      </div>
    </div>

    <div v-if="awardModal" class="fixed inset-0 z-[145]">
      <div class="acc-modal-overlay" @click="awardModal = null" />
      <div class="absolute inset-0 flex items-center justify-center p-4">
        <div class="acc-modal max-w-lg">
          <div class="flex flex-col items-center text-center">
            <div
              class="award-badge award-badge--modal relative overflow-visible rounded-full border-2 shadow-sm"
              :class="awardBadgeClasses(awardModal)"
            >
              <div class="award-badge__surface">
                <img
                  v-if="hasAwardImage(awardModal)"
                  :src="awardModal.image"
                  :alt="awardModal.name"
                  class="award-badge__art object-contain"
                  :class="awardArtworkClasses(awardModal)"
                  @error="markImageFailed(awardModal.id)"
                />
                <div
                  v-else
                  class="award-badge__art flex items-center justify-center text-5xl font-extrabold text-white sm:text-6xl"
                  :class="awardArtworkClasses(awardModal)"
                  :style="{ backgroundColor: awardModal.color || '#8b5cf6' }"
                >
                  {{ awardInitial(awardModal) }}
                </div>
              </div>
              <span
                v-if="shouldShowAwardLock(awardModal)"
                class="award-badge__lock flex items-center justify-center rounded-full border-2 border-foxy-background bg-foxy-cocoa text-white shadow-sm"
                :class="awardLockClasses(awardModal)"
                aria-hidden="true"
              >
                <i class="fas fa-lock !text-[#fffaf5] dark:!text-white" aria-hidden="true"></i>
              </span>
            </div>
            <div class="mt-4 min-w-0 w-full">
              <div class="text-lg font-extrabold leading-snug sm:text-xl">{{ awardModal.name }}</div>
              <div
                class="mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold"
                :class="awardStatusClasses(awardModal)"
              >
                {{ awardStatus(awardModal) }}
              </div>
              <div v-if="awardModal.points > 0" class="mt-1 text-xs text-slate-600 dark:text-white/60">
                +{{ formatCount(awardModal.points, 'бал', 'бали', 'балів') }}
              </div>
            </div>
          </div>

          <div v-if="awardModal.description" class="mt-3 text-sm text-slate-700 dark:text-white/80">
            {{ awardModal.description }}
          </div>
          <div
            v-if="awardPresentationState(awardModal) === 'earned' && (awardModal.awarded_at_human || awardModal.awarded_at)"
            class="mt-3 text-xs text-slate-500 dark:text-white/60"
          >
            Отримано: {{ awardModal.awarded_at_human || awardModal.awarded_at }}
          </div>
          <div class="mt-4 flex justify-end">
            <button class="acc-btn" type="button" @click="awardModal = null">Закрити</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import { formatCount } from '../../utils/ukrainianPluralization';

export default {
  name: 'ProfileAwards',
  props: {
    dashboard: { type: Object, default: null },
  },
  data() {
    return {
      awardModal: null,
      failedImages: {},
    };
  },
  computed: {
    catalog() {
      return this.dashboard?.award_catalog || {
        earned_count: 0,
        catalog_count: 0,
        standalone: [],
        series: [],
      };
    },
    sections() {
      const sections = [];
      const standalone = this.catalog.standalone || [];

      if (standalone.length) {
        sections.push({
          key: 'standalone',
          name: 'Окремі нагороди',
          description: null,
          isSeries: false,
          awards: standalone,
        });
      }

      return sections.concat((this.catalog.series || []).map((series) => ({
        ...series,
        key: `series-${series.id}`,
        isSeries: true,
      })));
    },
  },
  methods: {
    formatCount,
    openAwardModal(award) {
      this.awardModal = award || null;
    },
    hasAwardImage(award) {
      return Boolean(award?.image) && !this.failedImages[award.id];
    },
    markImageFailed(awardId) {
      this.$set(this.failedImages, awardId, true);
    },
    awardInitial(award) {
      return (award?.name || '?').slice(0, 1).toUpperCase();
    },
    awardPresentationState(award) {
      if (award?.earned === true) return 'earned';
      if (award?.is_obtainable === true) return 'available';
      return 'unavailable';
    },
    awardStatus(award) {
      const state = this.awardPresentationState(award);

      if (state === 'earned') return 'Отримано';
      if (state === 'available') return 'Можна отримати';
      return 'Зараз недоступна';
    },
    awardCardClasses(award) {
      const state = this.awardPresentationState(award);

      if (state === 'earned') {
        return 'border-[#c56925]/40 bg-[#c56925]/10 shadow-sm hover:border-[#c56925]/55 hover:bg-[#c56925]/15';
      }
      if (state === 'available') {
        return 'border-[#5b382a]/25 bg-white/35 hover:border-[#5b382a]/40 hover:bg-white/50 dark:border-white/15 dark:bg-white/5 dark:hover:border-[#5b382a]/35 dark:hover:bg-white/10';
      }
      return 'border-slate-400/30 bg-slate-300/20 hover:border-slate-400/40 hover:bg-slate-300/25 dark:border-slate-400/20 dark:bg-slate-500/5 dark:hover:border-slate-400/30 dark:hover:bg-slate-500/10';
    },
    awardStatusClasses(award) {
      const state = this.awardPresentationState(award);

      if (state === 'earned') {
        return 'bg-[#c56925]/15 text-[#9a4b18] dark:text-orange-300';
      }
      if (state === 'available') {
        return 'bg-[#5b382a]/10 text-[#5b382a] dark:bg-white/10 dark:text-[#e4c9bd]';
      }
      return 'bg-slate-500/15 text-slate-600 dark:text-slate-300';
    },
    awardBadgeClasses(award) {
      const state = this.awardPresentationState(award);

      if (state === 'earned') {
        return 'award-badge--earned border-[#c56925]/55 bg-[#efece9]/80 dark:bg-white/10';
      }
      if (state === 'available') {
        return 'award-badge--available border-[#5b382a]/25 bg-[#efece9]/60 dark:border-white/20 dark:bg-white/5';
      }
      return 'award-badge--unavailable border-slate-400/25 bg-slate-200/60 dark:bg-white/5';
    },
    awardArtworkClasses(award) {
      return `award-badge__art--${this.awardPresentationState(award)}`;
    },
    shouldShowAwardLock(award) {
      return this.awardPresentationState(award) !== 'earned';
    },
    awardLockClasses(award) {
      return `award-badge__lock--${this.awardPresentationState(award)}`;
    },
    sectionProgress(section) {
      if (section?.is_open_ended) {
        return `Зібрано ${this.formatAwardCount(section.earned_count || 0)}`;
      }
      return `${section?.earned_count || 0} з ${section?.total_count || 0}`;
    },
    isCompletedSeries(section) {
      return !section?.is_open_ended
        && Number(section?.total_count || 0) > 0
        && Number(section?.earned_count || 0) === Number(section?.total_count || 0);
    },
    formatAwardCount(count) {
      const value = Number(count || 0);
      const lastTwo = value % 100;
      const last = value % 10;
      let word = 'нагород';

      if (lastTwo < 11 || lastTwo > 14) {
        if (last === 1) word = 'нагороду';
        if (last >= 2 && last <= 4) word = 'нагороди';
      }

      return `${value} ${word}`;
    },
  },
};
</script>

<style scoped>
.award-badge {
  box-sizing: border-box;
  flex: 0 0 var(--award-badge-size);
  width: var(--award-badge-size);
  min-width: var(--award-badge-size);
  max-width: var(--award-badge-size);
  height: var(--award-badge-size);
  min-height: var(--award-badge-size);
  max-height: var(--award-badge-size);
  aspect-ratio: 1 / 1;
}

.award-badge--card {
  --award-badge-size: 92px;
  padding: 6px;
}

.award-badge--modal {
  --award-badge-size: min(160px, calc(100vw - 4rem));
  padding: 8px;
}

.award-badge__surface {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 9999px;
}

.award-badge__surface::after {
  position: absolute;
  inset: 0;
  border-radius: inherit;
  content: '';
  pointer-events: none;
}

.award-badge__art {
  display: flex;
  width: 100%;
  max-width: 100%;
  height: 100%;
  max-height: 100%;
  aspect-ratio: 1 / 1;
  border-radius: inherit;
  object-fit: contain;
}

.award-badge__art--earned {
  filter: none;
  opacity: 1;
}

.award-badge__art--available {
  filter: grayscale(0.82) saturate(0.28) brightness(0.92) contrast(0.94);
}

.award-badge__art--unavailable {
  filter: grayscale(1) saturate(0.05) brightness(0.64) contrast(0.9);
}

.award-badge--available .award-badge__surface::after {
  background: rgb(31 27 25 / 8%);
}

.award-badge--unavailable .award-badge__surface::after {
  background: rgb(31 27 25 / 24%);
}

.award-badge__lock {
  position: absolute;
  bottom: 3%;
  left: 50%;
  z-index: 1;
  width: 28px;
  height: 28px;
  font-size: 0.75rem;
  line-height: 1;
  pointer-events: none;
  transform: translate(-50%, 24%);
}

.award-badge__lock--available {
  opacity: 0.88;
}

.award-badge__lock--unavailable {
  width: 30px;
  height: 30px;
  opacity: 1;
}

.award-badge--modal .award-badge__lock {
  width: 38px;
  height: 38px;
  font-size: 1rem;
}

.award-badge--modal .award-badge__lock--unavailable {
  width: 42px;
  height: 42px;
  font-size: 1.125rem;
}

@media (min-width: 640px) {
  .award-badge--card {
    --award-badge-size: 112px;
  }

  .award-badge--modal {
    --award-badge-size: 208px;
    padding: 10px;
  }
}
</style>
